(() => {
  if (customElements.get('unik-season-parallax')) return;

  const MAX_OFFSET_PX = 50;

  class UnikSeasonParallax extends HTMLElement {
    connectedCallback() {
      this.image = this.querySelector('.unik-season__image');
      if (!this.image || !('IntersectionObserver' in window) || !('ResizeObserver' in window)) return;

      this.frame = null;
      this.visible = false;
      this.motion = window.matchMedia('(min-width: 750px) and (prefers-reduced-motion: no-preference)');
      this.schedule = () => {
        if (!this.isConnected || !this.motion.matches || !this.visible || document.hidden || this.frame !== null) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.intersection = new IntersectionObserver(entries => {
        if (!this.isConnected || !this.motion.matches) return;
        this.visible = entries[0].isIntersecting;
        if (this.visible) {
          window.addEventListener('scroll', this.schedule, { passive: true });
          this.schedule();
        } else {
          this.stop();
        }
      });
      this.resize = new ResizeObserver(this.schedule);
      this.configure = () => {
        this.intersection.disconnect();
        this.resize.disconnect();
        this.visible = false;
        this.stop();
        this.image.removeAttribute('data-parallax-active');
        this.image.style.removeProperty('--unik-season-parallax-transform');
        this.image.style.removeProperty('--unik-season-parallax-focus-x');
        if (!this.motion.matches) return;
        this.intersection.observe(this);
        this.resize.observe(this);
        this.resize.observe(this.image);
      };

      this.motion.addEventListener('change', this.configure);
      window.addEventListener('resize', this.schedule, { passive: true });
      document.addEventListener('visibilitychange', this.schedule);
      this.configure();
    }

    update() {
      const bounds = this.getBoundingClientRect();
      const imageHeight = this.image.clientHeight;
      if (!this.motion.matches || bounds.bottom <= 0 || bounds.top >= window.innerHeight || !imageHeight) return;

      const top = bounds.top + window.scrollY;
      const start = Math.max(0, top - window.innerHeight);
      const distance = top + bounds.height - start;
      const progress = Math.min(1, Math.max(0, (window.scrollY - start) / distance));
      const offset = MAX_OFFSET_PX * progress;
      const scale = 1 + offset / imageHeight;
      const focusX = getComputedStyle(this.image).objectPosition.split(' ')[0];

      this.image.style.setProperty('--unik-season-parallax-focus-x', focusX);
      this.image.style.setProperty('--unik-season-parallax-transform', `translate3d(0, ${offset}px, 0) scale(${scale})`);
      this.image.setAttribute('data-parallax-active', '');
    }

    stop() {
      window.removeEventListener('scroll', this.schedule);
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    disconnectedCallback() {
      if (!this.motion) return;
      this.visible = false;
      this.stop();
      this.intersection.disconnect();
      this.resize.disconnect();
      this.motion.removeEventListener('change', this.configure);
      window.removeEventListener('resize', this.schedule);
      document.removeEventListener('visibilitychange', this.schedule);
      this.image.removeAttribute('data-parallax-active');
      this.image.style.removeProperty('--unik-season-parallax-transform');
      this.image.style.removeProperty('--unik-season-parallax-focus-x');
    }
  }

  customElements.define('unik-season-parallax', UnikSeasonParallax);
})();
